import api from '../apiClient'
import type { Service } from '../../models/service'

interface ServicesApiResponse {
  success: boolean
  data: Service[]
}

interface SystemDefault {
  id: number
  name: string
  value: string
  created_at?: string
  updated_at?: string
}

interface SystemDefaultsApiResponse {
  success: boolean
  data: SystemDefault[]
}

export const getUserPurchases = async (): Promise<Service[]> => {
  const { data } = await api.get<ServicesApiResponse>('/services/user-purchases')
  
  if (data.success && data.data) {
    return data.data
  }
  
  throw new Error('Failed to fetch user purchases')
}

export const getServices = async (): Promise<Service[]> => {
  const { data } = await api.get<ServicesApiResponse>('/services/all')
  
  if (data.success && data.data) {
    return data.data
  }
  
  throw new Error('Failed to fetch services')
}

export const getSystemDefaults = async (): Promise<SystemDefault[]> => {
  const { data } = await api.get<SystemDefaultsApiResponse>('/system-defaults/all')
  
  if (data.success && data.data) {
    return data.data
  }
  
  throw new Error('Failed to fetch system defaults')
}

export const getCryptoAddress = async (): Promise<string | null> => {
  try {
    const systemDefaults = await getSystemDefaults()
    const cryptoAddressDefault = systemDefaults.find(item => item.name === 'crypto_address')
    return cryptoAddressDefault?.value || null
  } catch (error) {
    console.error('Failed to fetch crypto address:', error)
    return null
  }
}

export const initializePurchase = async (serviceId: number) => {
  const { data } = await api.post<{ success: boolean; transaction_id: string; message?: string }>(
    '/services/purchase',
    { service_id: serviceId },
  )
  return data
}

export const confirmPurchase = async (transactionId: string) => {
  const { data } = await api.post<{ success: boolean; message?: string }>(
    '/services/confirm-purchase',
    { txid: transactionId },
  )
  return data
}

export const cancelPurchase = async (transactionId: string) => {
  const { data } = await api.post<{ success: boolean; message?: string }>(
    '/services/cancel-purchase',
    { transaction_id: transactionId },
  )
  return data
}

// Legacy functions for backward compatibility
export const purchaseService = async (serviceId: number) => {
  const { data } = await api.post<{ sessionId: string; address: string; qr: string }>(
    '/services/purchase',
    { serviceId },
  )
  return data
}

export const verifyPayment = async (sessionId: string) => {
  const { data } = await api.post<{ verified: boolean }>('/services/verify-payment', { sessionId })
  return data
}
